import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { WebOnlyRoute } from './components/Auth/WebOnlyRoute';
import { LandingPage } from './components/LandingPage/LandingPage';
import { LoginScreen } from './components/Auth/LoginScreen';
import { RegisterScreen } from './components/Auth/RegisterScreen';
import { ForgotPassword } from './components/Auth/ForgotPassword';
import { UpdatePassword } from './components/Auth/UpdatePassword';
import { AuthCallback } from './components/Auth/AuthCallback';
import { GameDashboard } from './components/GameLayout';
import { SettingsPage } from './components/Settings/SettingsPage';
import { ProfilePage } from './components/Profile/ProfilePage';
import { PrivacyPolicy } from './components/StaticPages/Legal/PrivacyPolicy';
import { TermsOfService } from './components/StaticPages/Legal/TermsOfService';
import { Impressum } from './components/StaticPages/Legal/Impressum';
import { ComingSoonPage } from './components/StaticPages/Marketing/ComingSoonPage';
import { AboutUs } from './components/StaticPages/Marketing/AboutUs';
import { ScienceOfTime } from './components/StaticPages/Education/ScienceOfTime';
import { BlogIndex } from './components/StaticPages/Blog/BlogIndex';
import { BlogPostAbstractTime } from './components/StaticPages/Blog/BlogPostAbstractTime';
import { BlogPostRoutineEngine } from './components/StaticPages/Blog/BlogPostRoutineEngine';
import { BlogPostVisualTools } from './components/StaticPages/Blog/BlogPostVisualTools';
import { NotFound } from './components/StaticPages/NotFound';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './styles/globals.scss';
import { MainLayout } from './components/Layout/MainLayout';
// New Rive-based Squiggle system
import { SquiggleProvider } from './components/Squiggle';
import { registerDeepLinkHandler } from './utils/capacitorDeepLink';

const queryClient = new QueryClient();

// Tizen TV (and any file:// packaged build) loads from the filesystem, where the
// history API can't match server-style paths like "/app". The build:tv script
// sets REACT_APP_ROUTER=hash to select HashRouter; web/iOS builds use BrowserRouter.
const Router = process.env.REACT_APP_ROUTER === 'hash' ? HashRouter : BrowserRouter;

const DeepLinkBridge: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    void registerDeepLinkHandler(navigate);
  }, [navigate]);
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            {/* New Rive-based Squiggle mascot system */}
            <SquiggleProvider>
              <DeepLinkBridge />
              <Routes>
                {/* ============================================
                    PUBLIC WEBSITE ROUTES
                    These pages are accessible without authentication
                    ============================================ */}

                {/* Landing Page - Only visible on Web (Desktop/Mobile), redirects to login on Native Apps */}
                <Route path="/" element={
                  <WebOnlyRoute>
                    <LandingPage />
                  </WebOnlyRoute>
                } />

                {/* Authentication Routes */}
                <Route path="/login" element={<LoginScreen />} />
                <Route path="/register" element={<RegisterScreen />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Legal Pages - Accessible on all platforms */}
                <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                <Route path="/legal/terms" element={<TermsOfService />} />
                <Route path="/legal/impressum" element={<Impressum />} />
                <Route path="/legal/cookies" element={<ComingSoonPage title="Cookie Settings" emoji="🍪" />} />

                {/* Marketing & Content Pages - Web Only */}
                <Route path="/about" element={
                  <WebOnlyRoute>
                    <AboutUs />
                  </WebOnlyRoute>
                } />
                <Route path="/careers" element={
                  <WebOnlyRoute>
                    <ComingSoonPage title="Careers" emoji="💼" />
                  </WebOnlyRoute>
                } />
                <Route path="/science-of-time" element={
                  <WebOnlyRoute>
                    <ScienceOfTime />
                  </WebOnlyRoute>
                } />
                <Route path="/download" element={
                  <WebOnlyRoute>
                    <ComingSoonPage title="Download App" emoji="📱" />
                  </WebOnlyRoute>
                } />
                <Route path="/pricing" element={
                  <WebOnlyRoute>
                    <ComingSoonPage title="Pricing" emoji="💎" />
                  </WebOnlyRoute>
                } />

                {/* Blog - Web Only */}
                <Route path="/blog" element={
                  <WebOnlyRoute>
                    <BlogIndex />
                  </WebOnlyRoute>
                } />
                <Route path="/blog/abstract-time" element={
                  <WebOnlyRoute>
                    <BlogPostAbstractTime />
                  </WebOnlyRoute>
                } />
                <Route path="/blog/routine-engine" element={
                  <WebOnlyRoute>
                    <BlogPostRoutineEngine />
                  </WebOnlyRoute>
                } />
                <Route path="/blog/visual-tools" element={
                  <WebOnlyRoute>
                    <BlogPostVisualTools />
                  </WebOnlyRoute>
                } />

                {/* ============================================
                    AUTHENTICATED APP ROUTES
                    Protected routes wrapped in MainLayout shell
                    ============================================ */}
                <Route path="/app/*" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Routes>
                        {/* Dashboard - Main app entry point with clock/list/calendar views */}
                        <Route index element={<GameDashboard />} />

                        {/* User Settings & Profile */}
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="profile" element={<ProfilePage />} />

                        {/* Redirect legacy routes to dashboard */}
                        <Route path="tasks" element={<Navigate to="/app" replace />} />
                        <Route path="routines" element={<Navigate to="/app" replace />} />
                        <Route path="command_center" element={<Navigate to="/app" replace />} />
                      </Routes>
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* 404 - Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </SquiggleProvider>
          </Router>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
